import { ExperiencePurchaseResponse } from 'types/experienceCatalog';

/*
 * O que aconteceu com um pedido de resgate, do ponto de vista do modal.
 *
 * O modal tem um painel por caso: `confirmada` não tem painel (a página vai para o feedback),
 * `esgotada` e `recusada` explicam por que a vaga não veio, e `sem_confirmacao` cobre o caso
 * em que ninguém sabe se o resgate gravou — a chamada caiu, ou voltou sem `status`.
 */
export type PurchaseOutcome =
  | { kind: 'confirmada' }
  | { kind: 'esgotada' }
  | { kind: 'recusada'; message: string }
  | { kind: 'sem_confirmacao' };

export const GENERIC_REFUSAL_MESSAGE = 'Não foi possível realizar o resgate desta experiência.';

const SOLD_OUT_RESTRICTION = 'item_sold_out';

/*
 * O mapa de mensagens vem de fora porque mora em `index.tsx`: `vigencia-periodo.spec.ts` prende
 * a chave `catalog_period_closed` naquele arquivo, junto do código gravado pela trigger.
 */
export function resolvePurchaseOutcome(
  response: ExperiencePurchaseResponse | undefined,
  restrictionMessages: Record<string, string>,
): PurchaseOutcome {
  if (!response?.status) {
    return { kind: 'sem_confirmacao' };
  }

  if (response.status === 'OK') {
    return { kind: 'confirmada' };
  }

  if (response.status === 'UNAUTHORIZED') {
    // Esgotada vence as demais restrições: é a única recusa com painel próprio, e a que o
    // jogador precisa entender para escolher outra experiência.
    if (response.restrictions?.includes(SOLD_OUT_RESTRICTION)) {
      return { kind: 'esgotada' };
    }

    const knownRestriction = Object.keys(restrictionMessages).find(restriction => response.restrictions?.includes(restriction));

    return {
      kind: 'recusada',
      message: knownRestriction ? restrictionMessages[knownRestriction] : GENERIC_REFUSAL_MESSAGE,
    };
  }

  return {
    kind: 'recusada',
    message: response.message ? `Erro ao resgatar o item: ${response.message}` : 'Erro ao resgatar o item. Tente novamente.',
  };
}

interface RedeemOptions {
  purchase: () => Promise<ExperiencePurchaseResponse>;
  restrictionMessages: Record<string, string>;
  /** Resgate gravado: a página sai do catálogo. */
  onConfirmed: () => void;
  /** A confirmação não chegou: a página confere se o resgate gravou mesmo assim. */
  onUnconfirmed: () => Promise<void>;
}

/*
 * Envia o resgate, classifica a resposta e aciona a página, nesta ordem.
 *
 * A sequência mora aqui, e não em `index.tsx`, para ser testada rodando. Na página ela só podia
 * ser presa por gate de texto, e um `return` antecipado passava pelo gate com o modal preso em
 * "processando". A conferência é aguardada: o modal só mostra "Não recebemos a confirmação"
 * depois de a página ter tido a chance de levar o jogador ao feedback.
 */
export async function redeemExperience({
  purchase,
  restrictionMessages,
  onConfirmed,
  onUnconfirmed,
}: RedeemOptions): Promise<PurchaseOutcome> {
  let outcome: PurchaseOutcome;
  try {
    outcome = resolvePurchaseOutcome(await purchase(), restrictionMessages);
  } catch (error) {
    console.error('Error purchasing experience:', error);
    outcome = { kind: 'sem_confirmacao' };
  }

  if (outcome.kind === 'confirmada') {
    onConfirmed();
  }

  if (outcome.kind === 'sem_confirmacao') {
    await onUnconfirmed();
  }

  return outcome;
}
